import { decamelizeKeys } from "humps";
import { ProcessingOverlay } from "PFApp/components/activity_form/multi_activity_form/processing_overlay";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { ManageRolesStoreProvider, useManageRolesStore } from "PFApp/stores/manage_roles";
import { useManageViewRoles } from "PFCore/hooks/queries/roles/use_manage_view_roles";
import { AvailabilityMode } from "PFCore/types/booking";
import { ManageRolesViewRoleItem } from "PFCore/types/manage_roles_view";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { useUnmount } from "react-use";

import { parseJsonArrayIds } from "./manage_roles.utils";
import { MAX_ENGAGEMENT_IDS, MAX_ROLE_IDS, ROLES_PER_PAGE } from "./manage_roles_constants";
import { ManageRolesPageContent } from "./manage_roles_page";
import { ManageRolesFormProvider } from "./parts/manage_roles_form_provider";
import { SuggestedWMSidePanel } from "./parts/suggested_wm_side_panel";
import { useSuggestedWmSidePanelActions } from "./parts/suggested_wm_side_panel/use_suggested_wm_side_panel_actions";

const ROLE_IDS_QUERY_PARAM = "roleIds";
const ENGAGEMENT_IDS_QUERY_PARAM = "engagementIds";

type ManageRolesPageInnerProps = {
  engagementIds: number[];
  roleIds: number[];
};

const ManageRolesPageInner = ({ engagementIds, roleIds }: ManageRolesPageInnerProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "manageRoles" });

  const currentPage = useManageRolesStore((state) => state.currentPage);
  const { closeSuggestedWmSidePanel } = useSuggestedWmSidePanelActions();

  useUnmount(closeSuggestedWmSidePanel);

  const { data, isLoading } = useManageViewRoles(
    {
      targetAvailabilityMode: AvailabilityMode.PerWeek,
      page: currentPage,
      perPage: ROLES_PER_PAGE,
      ...(engagementIds.length > 0 ? { engagementIds } : {}),
      ...(roleIds.length > 0 ? { roleIds } : {})
    },
    {
      select: (data) => ({
        entries: decamelizeKeys(data.entries) as any,
        meta: data.meta
      })
    }
  );

  const entries: ManageRolesViewRoleItem[] = data?.entries ?? [];
  const totalPages = data?.meta.totalPages ?? 1;
  const totalRoles = data?.meta.total ?? 0;

  return (
    <>
      {isLoading && (
        <ProcessingOverlay title={t("preparingRoles")} subtitle={t("preparingRolesDescription")} />
      )}
      <ManageRolesFormProvider entries={entries}>
        <ManageRolesPageContent totalRoles={totalRoles} totalPages={totalPages} />
      </ManageRolesFormProvider>
      <SuggestedWMSidePanel />
    </>
  );
};

export const ManageRolesPageController = () => {
  const { t } = useTranslation("activities", { keyPrefix: "manageRoles" });
  const { showGrowl } = useGrowlStoreActions();

  const [searchParams] = useSearchParams();
  const rawRoleIds = parseJsonArrayIds(searchParams.get(ROLE_IDS_QUERY_PARAM));
  const rawEngagementIds = parseJsonArrayIds(searchParams.get(ENGAGEMENT_IDS_QUERY_PARAM));

  const roleIdsTruncated = rawRoleIds.length > MAX_ROLE_IDS;
  const engagementIdsTruncated = rawEngagementIds.length > MAX_ENGAGEMENT_IDS;

  useEffect(() => {
    if (roleIdsTruncated || engagementIdsTruncated) {
      showGrowl({ message: t("paramsTruncated"), kind: "alert" });
    }
  }, [roleIdsTruncated, engagementIdsTruncated, showGrowl, t]);

  const engagementIds = rawEngagementIds.slice(0, MAX_ENGAGEMENT_IDS);
  const roleIds = rawRoleIds.slice(0, MAX_ROLE_IDS);

  return (
    <ManageRolesStoreProvider>
      <ManageRolesPageInner engagementIds={engagementIds} roleIds={roleIds} />
    </ManageRolesStoreProvider>
  );
};
